import { FC, useMemo } from 'react';

import { useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { localizationSelector } from 'store/localizationState';
import { useGetServicesListQuery } from 'store/serviceService';
import { authSelector } from 'store/authState';

import SeoHelmet from 'components/fragments/SeoHelmet';
import Copyright from 'components/UI/Copyright';
import Heading, { Type } from 'components/UI/Heading';
import Loader from 'components/UI/Loader';
import { IconType } from 'components/UI/Icon';
import PageState from 'components/UI/Page/PageState';
import PageWrapper from 'components/UI/Page/PageWrapper';
import { ServiceCard } from 'components/UI/ServiceCard';
import { ServiceAccess } from 'models/service';

import { HeadingContainer, Description, List } from './styled';

export const ServicesPage: FC = () => {
  const t = useTranslations();
  const { session } = useAppSelector(authSelector);
  const { languageCode } = useAppSelector(localizationSelector);
  const { data, isLoading } = useGetServicesListQuery({
    languageCode,
    countryCode: session.user?.attributes.country
  });

  // Active services
  const activeServices = useMemo(() => {
    const services =
      data?.filter((service) => service.access === ServiceAccess.APPROVED) ??
      [];
    if (!services.length) {
      return [];
    }
    return (
      <div>
        <Heading type={Type.h2}>{t('myservices.section_active_title')}</Heading>
        <List>
          {services.map((service) => (
            <ServiceCard
              key={service.iamGroup}
              service={service}
              isService={{ approved: true }}
            />
          ))}
        </List>
      </div>
    );
  }, [t, data]);

  // Pending services
  const pendingServices = useMemo(() => {
    const services =
      data?.filter((service) => service.access === ServiceAccess.PENDING) ?? [];
    if (!services.length) {
      return null;
    }
    return (
      <div>
        <Heading type={Type.h2}>
          {t('myservices.section_pending_title')}
        </Heading>
        <List>
          {services.map((service) => (
            <ServiceCard
              key={service.iamGroup}
              service={service}
              isService={{ pending: true }}
            />
          ))}
        </List>
      </div>
    );
  }, [t, data]);

  // Available services
  const availableServices = useMemo(() => {
    const services =
      data?.filter((service) => service.access === ServiceAccess.AVAILABLE) ??
      [];
    if (!services.length) {
      return null;
    }
    return (
      <div>
        <Heading type={Type.h2}>
          {t('myservices.section_available_title')}
        </Heading>
        <List>
          {services.map((service) => (
            <ServiceCard
              key={service.iamGroup}
              service={service}
              isService={{ available: true }}
            />
          ))}
        </List>
      </div>
    );
  }, [t, data]);

  // Services
  const serviceCards = useMemo(() => {
    // Loading
    if (isLoading) {
      return <Loader padding center size="large" />;
    }

    // Empty state
    if (!data?.length) {
      return (
        <PageState
          iconType={IconType.Products}
          title={t('myservices.empty_title')}
          description={t('myservices.empty_description')}
        />
      );
    }

    // Data
    return (
      <div>
        {activeServices}
        {pendingServices}
        {availableServices}
      </div>
    );
  }, [t, data, isLoading, activeServices, pendingServices, availableServices]);

  return (
    <PageWrapper>
      <Copyright>
        <SeoHelmet title="myservices.meta_title" />
        <HeadingContainer>
          <Heading>{t('myservices.title')}</Heading>
          {!!data?.length && <h6>{`(${data.length})`}</h6>}
        </HeadingContainer>
        <Description>{t('myservices.description')}</Description>
        {serviceCards}
      </Copyright>
    </PageWrapper>
  );
};
